// Wire types for the Roles admin surface. Field names are snake_case to
// match the backend JSON contract (same convention as session/types.ts).
//
// Per LLD 11 v0.2: rAZNx is gone — there is no `raznx_role_id` field on
// roles, and the catalog carries `name`, `description`, `is_dangerous`,
// and `is_reserved` per permission entry. The four reserved keys
// (`role:manage`, `rule:manage`, `tenant:manage`, `user:force_logout`)
// are held only by the `tenant_admin` system role.

export interface Role {
  role_id: string;
  tenant_id: string;
  display_name: string;
  description: string;
  permission_set: string[];
  catalog_version: string;
  is_system: boolean;
  version: number;
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
  deleted_at?: string;
}

export interface PermissionEntry {
  key: string;
  /**
   * Human-readable name. The backend may also emit `label` for legacy
   * compatibility — both fields point at the same string.
   */
  name?: string;
  label?: string;
  description?: string;
  is_dangerous: boolean;
  /**
   * Reserved permissions can only be held by `tenant_admin`. Custom-role
   * forms render these checkboxes disabled with a tooltip explaining the
   * restriction; the validator rejects any custom role that selects one
   * with `reserved_permission`.
   */
  is_reserved?: boolean;
}

export interface PermissionGroup {
  key: string;
  label: string;
  permissions: PermissionEntry[];
}

export interface PermissionCatalog {
  catalog_version: string;
  groups: PermissionGroup[];
}

export interface ReconcileSummary {
  grants_added: number;
  grants_removed: number;
  failures_count: number;
}

export interface AliasFailure {
  alias: string;
  error: string;
}

export interface RoleUser {
  alias: string;
  ring_user_id: string;
  location_id: string;
  granted_at: string;
}

export interface AuditEntry {
  event_id: string;
  event_type: string;
  actor: string;
  target: string;
  timestamp: string;
  details: Record<string, unknown>;
}

export interface ValidateRoleResult {
  ok: boolean;
  error?: string;
}

/**
 * Helper: read the display name for a permission entry, preferring `name`
 * (LLD 11 v0.2 catalog) and falling back to `label` for older payloads.
 */
export function permissionDisplayName(p: PermissionEntry): string {
  return p.name ?? p.label ?? p.key;
}
